import type { ProposalBodyRow } from '../api/types';
import type { ProposalData } from '../types/portal';

/**
 * The wizard's field names and the IRB tables' column names grew up apart.
 * Everything that translates between them lives here, so the screens can keep
 * the design's vocabulary and the API keeps the database's.
 */

/** Wizard narrative key → SaveProposal parameter. */
export const NARRATIVE_TO_PARAM: Record<string, keyof ProposalBodyRow> = {
  introduction: 'Introduction',
  rationale: 'Rationale',
  objectives: 'Objectives',
  studyTypeSetting: 'StudyType',
  sampleSize: 'SampleSize',
  samplingTechnique: 'SamplingTech',
  studyPopulation: 'StudyPopulation',
  inclusion: 'Inclusion',
  exclusion: 'ExclCriteria',
  instrument: 'StudyInstrument',
  procedure: 'ProcedureDet',
  dataAnalysis: 'DataAnalysis'
};

/**
 * Top-level ProposalData field → SaveProposal parameter.
 *
 * The parameter name and the column it lands in are not always the same word:
 * @Facilities writes ResProposals.InstitutionalFacilities, which is what
 * GetProposal hands back. FIELD_FROM_COLUMN below is the return trip, and the
 * two are deliberately separate rather than one map used both ways.
 */
export const FIELD_TO_PARAM: Record<string, string> = {
  references: 'ReferencesDet',
  facilities: 'Facilities',
  potentialBenefits: 'PotentialBenefits',
  potentialRisks: 'PotentialRisks',
  ethics: 'Ethical',
  dissemination: 'Dissemination'
};

/** Top-level ProposalData field ← column name in the GetProposal payload. */
export const FIELD_FROM_COLUMN: Record<string, string> = {
  references: 'ReferencesDet',
  facilities: 'InstitutionalFacilities',
  potentialBenefits: 'PotentialBenefits',
  potentialRisks: 'PotentialRisks',
  ethics: 'Ethical',
  dissemination: 'Dissemination'
};

export const MAX_GANTT_MONTHS = 24;
export const DEFAULT_GANTT_MONTHS = 6;

/** How many month columns the Gantt grid should show for a given duration. */
export function ganttMonthCount(durationMonths: string): number {
  const n = Number(durationMonths);
  if (!Number.isFinite(n) || n < 1) return DEFAULT_GANTT_MONTHS;
  return Math.min(MAX_GANTT_MONTHS, Math.max(DEFAULT_GANTT_MONTHS, Math.ceil(n)));
}

/** Pads or trims a month array to `count` entries. */
export function fitMonths(months: boolean[], count: number): boolean[] {
  const out = months.slice(0, count);
  while (out.length < count) out.push(false);
  return out;
}

/**
 * Status id on Web_Students.dbo.Researchs → the approval stage the dashboard
 * timeline highlights. The IRB flow chart has more stages than the database has
 * statuses, so several statuses land on the same stage.
 */
export const STATUS_TO_STAGE: Record<string, string> = {
  '01': 'submission',
  '02': 'src-office',
  '03': 'src-review',
  '04': 'rc-office',
  '05': 'irb-office-intake',
  '06': 'irb-meeting',
  '07': 'irb-meeting',
  '08': 'irb-office-intake',
  '09': 'irb-meeting',
  '10': 'letter',
  '11': 'irb-office-decision'
};

/** True when the proposal has come back to the researcher for correction. */
export function isSentBack(statusId: string): boolean {
  return statusId === '03' || statusId === '07';
}

export function isDecided(statusId: string): boolean {
  return statusId === '10' || statusId === '11';
}

export function isApproved(statusId: string): boolean {
  return statusId === '10';
}

/** Reads the m1…m24 columns off a Gantt row the API returned. */
export function monthsFromRow(row: Record<string, unknown>, count: number): boolean[] {
  const out: boolean[] = [];
  for (let i = 1; i <= count; i += 1) {
    out.push(row[`m${i}`] === true || row[`m${i}`] === 1);
  }
  return out;
}

/** Builds the SaveProposal payload from the wizard's working copy. */
export function toSaveProposalBody(data: ProposalData): Record<string, string> {
  const body: Record<string, string> = {
    Title: data.title,
    ReviewCategory: data.reviewCategory,
    ProjectTypeIds: JSON.stringify(data.projectTypes),
    StartDate: data.startDate,
    DurationMonths: data.durationMonths
  };

  Object.entries(NARRATIVE_TO_PARAM).forEach(([key, param]) => {
    body[param as string] = data.narratives[key] ?? '';
  });

  Object.entries(FIELD_TO_PARAM).forEach(([key, param]) => {
    body[param] = (data as unknown as Record<string, string>)[key] ?? '';
  });

  return body;
}

/** Rebuilds the wizard's working copy from what the API returned. */
export function fromProposalBody(
  body: ProposalBodyRow | null,
  base: ProposalData
): ProposalData {
  if (!body) return base;

  const narratives: Record<string, string> = { ...base.narratives };
  Object.entries(NARRATIVE_TO_PARAM).forEach(([key, param]) => {
    narratives[key] = String(body[param] ?? '');
  });

  const fields: Record<string, string> = {};
  Object.entries(FIELD_FROM_COLUMN).forEach(([key, column]) => {
    fields[key] = String((body as unknown as Record<string, unknown>)[column] ?? '');
  });

  return {
    ...base,
    ...fields,
    title: body.Title ?? '',
    reviewCategory: (body.ReviewCategory ?? '') as ProposalData['reviewCategory'],
    startDate: normaliseDateForInput(body.StartDate ?? ''),
    durationMonths: body.DurationMonths ?? '',
    narratives
  } as ProposalData;
}

/**
 * The legacy column is free text, so a stored date can be "2026-11-01",
 * "01/11/2026" or anything a person typed. An <input type="date"> only accepts
 * yyyy-mm-dd, so anything else is handed back blank rather than silently wrong.
 */
export function normaliseDateForInput(value: string): string {
  const raw = (value ?? '').trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  const dmy = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(
      parsed.getDate()
    ).padStart(2, '0')}`;
  }
  return '';
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString([], {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}
